import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../../config';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  getTranslatedNotificationContent,
  getNotificationNavigatePath,
  isPresenceHourlyLinkActive
} from '../../utils/notificationDisplay';
import { fetchPositionForPresenceConfirm, messageForPresenceGeoFailure } from '../../utils/presenceGeolocation';
import { Bell, ChevronRight, Loader2, Info, Calendar, Megaphone, FileText, DollarSign, Lightbulb, BookOpen, ClipboardList, Target, Award, CreditCard, MapPin } from 'lucide-react';

const typeIcons = {
  leave: Calendar,
  announcement: Megaphone,
  payroll: FileText,
  expense: DollarSign,
  idea: Lightbulb,
  formation: BookOpen,
  planning: ClipboardList,
  performance: Target,
  badge: Award,
  billing: CreditCard,
  presence_hourly_confirm: Bell,
  presence_escalation: Bell,
  presence_zone_change: MapPin,
  default: Bell
};

const MOBILE_BREAKPOINT = 768;

export function NotificationIcon({ onUnreadChange, socket }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT);
  const ref = useRef(null);

  const filterForPopover = (items) =>
    (items || []).filter((n) => {
      if (n?.type !== 'presence_hourly_confirm') return true;
      // Confirmed presence: hide from bell list; full history stays on /notifications.
      if (n?.presenceResponseStatus === 'responded') return false;
      // Keep unseen reminders in popover (even if expired), until user reads/views them.
      // Once read, keep only actionable pending reminders.
      if (!n?.read) return true;
      return n?.presenceResponseStatus === 'pending';
    });

  const confirmPresenceFromPopover = async (n) => {
    if (!n || n.type !== 'presence_hourly_confirm' || !n.relatedId) return;
    if (confirmingId) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const ok = window.confirm(
      t('notifications.presence_confirm_prompt') || 'Please confirm you are on site ?'
    );
    if (!ok) return;
    try {
      setConfirmingId(n.id);
      const geo = await fetchPositionForPresenceConfirm();
      if (!geo.ok) {
        alert(messageForPresenceGeoFailure(t, geo));
        return;
      }
      const res = await fetch(`${API_BASE}/api/attendance/presence/confirm`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ alertCycleId: n.relatedId, location: geo.position })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.error || t('notifications.presence_confirm_failed') || 'Presence confirmation failed.');
        return;
      }
      // Do not auto-mark as read; keep visible until user marks it manually.
      fetchUnread();
      fetchRecent();
    } catch (_e) {
      alert(t('notifications.presence_confirm_failed') || 'Presence confirmation failed.');
    } finally {
      setConfirmingId(null);
    }
  };

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  const fetchUnread = () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_BASE}/api/notifications?limit=100&offset=0&archived=false&unreadOnly=true`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success && Array.isArray(d.notifications)) {
          const visibleUnread = filterForPopover(d.notifications).length;
          setUnreadCount(visibleUnread);
          onUnreadChange && onUnreadChange(visibleUnread);
        }
      })
      .catch(() => {});
  };

  const fetchRecent = () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoadingRecent(true);
    fetch(`${API_BASE}/api/notifications?limit=10&offset=0&archived=false&unreadOnly=true`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success && Array.isArray(d.notifications)) setRecent(filterForPopover(d.notifications));
      })
      .catch(() => {})
      .finally(() => setLoadingRecent(false));
  };

  const markAsReadAndRefresh = (id, thenNavigate) => {
    const token = localStorage.getItem('token');
    if (!token) {
      if (thenNavigate) thenNavigate();
      return;
    }
    setRecent((prev) => prev.filter((x) => x.id !== id));
    setUnreadCount((c) => Math.max(0, c - 1));
    fetch(`${API_BASE}/api/notifications/${id}/read`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(() => {
        fetchUnread();
        fetchRecent();
      })
      .catch(() => {})
      .finally(() => {
        if (thenNavigate) thenNavigate();
      });
  };

  const markVisibleAsReadThenNavigate = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setOpen(false);
      navigate('/notifications');
      return;
    }
    const ids = (recent || []).filter((n) => !n.read).map((n) => n.id);
    try {
      if (ids.length > 0) {
        await Promise.all(
          ids.map((id) =>
            fetch(`${API_BASE}/api/notifications/${id}/read`, {
              method: 'PATCH',
              headers: { Authorization: `Bearer ${token}` }
            }).catch(() => null)
          )
        );
      }
    } finally {
      setRecent((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      onUnreadChange && onUnreadChange(0);
      setOpen(false);
      navigate('/notifications');
      fetchUnread();
      fetchRecent();
    }
  };

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    const onRefresh = () => {
      fetchUnread();
      if (open) fetchRecent();
    };
    const onFocus = () => onRefresh();
    const onVisibility = () => {
      if (!document.hidden) onRefresh();
    };
    window.addEventListener('notification-refresh', onRefresh);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      window.removeEventListener('notification-refresh', onRefresh);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [open]);

  useEffect(() => {
    if (socket) {
      const onNew = () => {
        fetchUnread();
        if (open) fetchRecent();
      };
      socket.on('notification:new', onNew);
      return () => socket.off('notification:new', onNew);
    }
  }, [socket, open]);

  useEffect(() => {
    if (open) {
      fetchUnread();
      fetchRecent();
      const openPoll = setInterval(() => {
        fetchUnread();
        fetchRecent();
      }, 10000);
      return () => clearInterval(openPoll);
    }
  }, [open]);

  useEffect(() => {
    const close = (e) => {
      if (confirmingId) return;
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) {
      document.addEventListener('click', close);
      return () => document.removeEventListener('click', close);
    }
  }, [open, confirmingId]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => {
          if (confirmingId) return;
          setOpen((o) => !o);
        }}
        aria-label="Notifications"
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          border: '1px solid rgba(255,255,255,0.2)',
          background: 'rgba(255,255,255,0.08)',
          color: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}
      >
        <Bell size={22} />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              minWidth: 18,
              height: 18,
              borderRadius: 9,
              background: '#ef4444',
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px'
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: isMobile ? 'fixed' : 'absolute',
            top: isMobile ? 72 : '100%',
            left: isMobile ? 12 : undefined,
            right: isMobile ? 12 : 0,
            marginTop: isMobile ? 0 : 12,
            width: isMobile ? undefined : 360,
            minWidth: 280,
            maxWidth: isMobile ? undefined : '92vw',
            maxHeight: 420,
            overflow: 'hidden',
            borderRadius: 12,
            background: 'linear-gradient(180deg, #1a1f3a 0%, #0f1329 100%)',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box'
          }}
        >
          <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 0 }}>
            <span
              role="button"
              tabIndex={0}
              onClick={() => {
                setOpen(false);
                navigate('/notifications');
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                setOpen(false);
                navigate('/notifications');
              }}
              style={{
                fontWeight: 600,
                color: '#fff',
                fontSize: 15,
                whiteSpace: 'nowrap',
                cursor: 'pointer'
              }}
              title={t('notifications.view_all')}
            >
              {t('notifications.title')}
            </span>
            {unreadCount > 0 && (
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
                {t('notifications.popover_unread', { count: unreadCount })}
              </span>
            )}
          </div>
          <div
            style={{
              padding: '8px 12px',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              fontSize: 11,
              color: 'rgba(255,255,255,0.45)',
              lineHeight: 1.35,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 6
            }}
          >
            <Info size={14} color="rgba(147, 197, 253, 0.7)" style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{t('notifications.retention_notice_popover')}</span>
          </div>
          <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, maxHeight: 320, minWidth: 0 }}>
            {loadingRecent ? (
              <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
                <Loader2 size={24} color="#94a3b8" style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            ) : recent.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: 14, whiteSpace: 'normal', wordBreak: 'normal' }}>
                {t('notifications.popover_empty')}
              </div>
            ) : (
              recent.map((n) => {
                const { title: displayTitle, body: displayBody } = getTranslatedNotificationContent(n, t);
                const Icon = typeIcons[n.type] || typeIcons.default;
                const path = getNotificationNavigatePath(n);
                const presenceStatus = n.presenceResponseStatus || '';
                const isHourlyPending =
                  n.type === 'presence_hourly_confirm' &&
                  presenceStatus === 'pending' &&
                  isPresenceHourlyLinkActive(n);
                const isHourlyPresence = n.type === 'presence_hourly_confirm';
                const hourlyLinkExpired =
                  isHourlyPresence && presenceStatus !== 'responded' && !isPresenceHourlyLinkActive(n);
                const linkInteractive = isHourlyPending
                  ? true
                  : Boolean(path) && isPresenceHourlyLinkActive(n) && !isHourlyPresence;
                const isConfirming = confirmingId === n.id;
                return (
                  <div
                    key={n.id}
                    role={linkInteractive ? 'button' : undefined}
                    tabIndex={linkInteractive ? 0 : undefined}
                    onClick={() => {
                      if (isConfirming) return;
                      if (!linkInteractive) return;
                      if (isHourlyPending) {
                        confirmPresenceFromPopover(n);
                        return;
                      }
                      if (isHourlyPresence) {
                        markAsReadAndRefresh(n.id);
                        return;
                      }
                      setOpen(false);
                      markAsReadAndRefresh(n.id, () => {
                        if (path) navigate(path);
                      });
                    }}
                    onKeyDown={(e) => {
                      if (isConfirming) return;
                      if (!linkInteractive || e.key !== 'Enter') return;
                      if (isHourlyPending) {
                        confirmPresenceFromPopover(n);
                        return;
                      }
                      if (isHourlyPresence) {
                        markAsReadAndRefresh(n.id);
                        return;
                      }
                      setOpen(false);
                      markAsReadAndRefresh(n.id, () => path && navigate(path));
                    }}
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                      cursor: linkInteractive ? 'pointer' : path ? 'not-allowed' : 'default',
                      background: 'rgba(59, 130, 246, 0.06)',
                      opacity: path && !linkInteractive ? 0.7 : 1
                    }}
                    title={
                      hourlyLinkExpired || (!isHourlyPresence && path && !linkInteractive)
                        ? t('notifications.presence_hourly_link_disabled')
                        : undefined
                    }
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <Icon size={18} color="#94a3b8" style={{ flexShrink: 0, marginTop: 2 }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: n.read ? 500 : 600, color: '#fff', fontSize: 14 }}>{displayTitle}</div>
                        {displayBody && !isConfirming && (
                          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>{displayBody}</div>
                        )}
                        {isConfirming && (
                          <div style={{ fontSize: 12, color: '#93c5fd', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                            {t('notifications.presence_confirm_processing') || 'Processing confirmation...'}
                          </div>
                        )}
                        {hourlyLinkExpired && (
                          <div style={{ fontSize: 11, color: '#f87171', marginTop: 4 }}>
                            {t('notifications.presence_hourly_link_disabled')}
                          </div>
                        )}
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                          {n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {recent.length > 0 && (
            <div
              style={{
                padding: '10px 16px',
                borderTop: '1px solid rgba(255,255,255,0.1)',
                textAlign: 'center'
              }}
            >
              <button
                type="button"
                onClick={markVisibleAsReadThenNavigate}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: 'none',
                  background: 'rgba(59, 130, 246, 0.25)',
                  color: '#93c5fd',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 500,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                {t('notifications.view_all')}
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
